/**
 * useMessages — React Query hooks for the messaging system.
 *
 * Responsibilities:
 *   - Read: conversations list + paginated message history (infinite query).
 *   - Write: send a message with optimistic UI; mark messages as read.
 *   - Live: subscribe to WS events `message:new` / `message:typing` /
 *     `message:read` and patch the cache so the UI updates without a refetch.
 *
 * Design notes:
 *   - Optimistic sends insert a placeholder Message with `status: 'sending'`
 *     into the first page of the infinite cache. On success the placeholder
 *     is replaced by the server message (matched by `clientId`). On failure
 *     it stays visible with `status: 'failed'` so the user can retry — we
 *     never silently drop a failed write.
 *   - De-dup on WS arrival: if the incoming message id is already in the
 *     cache (e.g. echoed back after our optimistic insert resolved), we drop
 *     it. This prevents double-render on reconnect when the server replays
 *     events.
 *   - Typing indicator state lives in this hook (per-conversation) and
 *     auto-clears after 5s of inactivity per the spec.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import * as messagesApi from '../services/messagesApi';
import type {
  Conversation,
  ConversationFilters,
  Message,
} from '../services/messagesApi';
import { wsService } from '../services/websocket';
import { getOidcProfile, getCachedUserId } from '../services/apiClient';

// =====================================================
// CACHE / KEY HELPERS
// =====================================================

export const messageKeys = {
  conversations: (filters: ConversationFilters = {}) =>
    ['messages', 'conversations', filters] as const,
  conversation: (id: string) => ['messages', 'conversation', id] as const,
  messages: (conversationId: string) =>
    ['messages', 'messages', conversationId] as const,
};

const PAGE_SIZE = 30;

/**
 * Optimistic-state mixin for messages held in the UI cache. The server type
 * is `Message` plain; we union with a `clientStatus` field that only exists
 * for entries we've just sent and not yet confirmed.
 */
export type OptimisticMessage = Message & {
  clientStatus?: 'sending' | 'failed';
  clientId?: string;
};

type MessagePages = InfiniteData<Message[]>;

// =====================================================
// CONVERSATIONS LIST
// =====================================================

export function useConversations(filters: ConversationFilters = {}) {
  return useQuery({
    queryKey: messageKeys.conversations(filters),
    queryFn: () => messagesApi.getConversations(filters),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * Aggregate unread badge: any conversation with unreadCount > 0?
 * Used by the layout nav to render a red dot.
 */
export function useHasUnreadMessages(): boolean {
  const { data } = useConversations();
  return useMemo(() => (data ?? []).some((c) => c.unreadCount > 0), [data]);
}

export function useConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: messageKeys.conversation(conversationId ?? ''),
    queryFn: () => messagesApi.getConversation(conversationId!),
    enabled: !!conversationId,
    staleTime: 30_000,
  });
}

// =====================================================
// MESSAGE HISTORY (infinite, paginated)
// =====================================================

/**
 * Returns messages newest-first across pages. The thread UI is responsible
 * for reversing for display (oldest at top).
 */
export function useMessages(conversationId: string | undefined) {
  return useInfiniteQuery<Message[], Error, MessagePages, ReturnType<typeof messageKeys.messages>, string | undefined>({
    queryKey: messageKeys.messages(conversationId ?? ''),
    queryFn: ({ pageParam }) =>
      messagesApi.getMessages(conversationId!, { before: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      // `lastPage` is newest-first; oldest is the cursor for the next page.
      return lastPage[lastPage.length - 1].sentAt;
    },
    enabled: !!conversationId,
    staleTime: 10_000,
    // The WS broadcaster for `message:new` isn't wired server-side yet, so poll
    // the open thread every 5s as a pragmatic fallback — this is what surfaces
    // a student's reply to the instructor without a manual reload. React Query
    // refetches only the first page (undefined cursor); older pages are stable.
    refetchInterval: 5_000,
  });
}

// =====================================================
// SEND MESSAGE (optimistic)
// =====================================================

interface SendMessageArgs {
  conversationId: string;
  body: string;
  /** Set when retrying a previously-failed optimistic message. */
  clientId?: string;
}

function makeClientId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation<Message, Error, SendMessageArgs, { clientId: string }>({
    mutationFn: ({ conversationId, body }) => messagesApi.sendMessage(conversationId, body),

    onMutate: async ({ conversationId, body, clientId }) => {
      await queryClient.cancelQueries({ queryKey: messageKeys.messages(conversationId) });
      const effectiveClientId = clientId ?? makeClientId();
      // Attribute the optimistic bubble to the real identity — the legacy
      // authStore user is empty under OIDC. senderId must be the backend DB
      // user id (so it still reads as 'instructor' once the server echo
      // replaces this via mapMessage), while the display name comes from the
      // OIDC profile claims.
      const profile = getOidcProfile();

      queryClient.setQueryData<MessagePages>(
        messageKeys.messages(conversationId),
        (old) => {
          const optimistic: OptimisticMessage = {
            id: effectiveClientId,
            clientId: effectiveClientId,
            clientStatus: 'sending',
            conversationId,
            senderId: getCachedUserId() || 'me',
            senderRole: 'instructor',
            senderName:
              profile?.name ||
              [profile?.given_name, profile?.family_name].filter(Boolean).join(' ') ||
              profile?.email ||
              'You',
            body,
            sentAt: new Date().toISOString(),
            readAt: null,
            edited: false,
          };

          if (!old) {
            return { pages: [[optimistic as Message]], pageParams: [undefined] };
          }
          // If retrying, replace the failed copy by clientId.
          const pages = old.pages.map((page, idx) => {
            if (idx !== 0) return page;
            const filtered = page.filter(
              (m) => (m as OptimisticMessage).clientId !== effectiveClientId
            );
            return [optimistic as Message, ...filtered];
          });
          return { ...old, pages };
        }
      );

      return { clientId: effectiveClientId };
    },

    onSuccess: (serverMessage, { conversationId }, ctx) => {
      const clientId = ctx?.clientId;
      // Replace the optimistic entry with the server-canonical record.
      queryClient.setQueryData<MessagePages>(
        messageKeys.messages(conversationId),
        (old) => {
          if (!old) return { pages: [[serverMessage]], pageParams: [undefined] };
          // If the WS already inserted this server id, drop our optimistic
          // copy but don't add a duplicate.
          const alreadyHasServerId = old.pages.some((page) =>
            page.some((m) => m.id === serverMessage.id)
          );
          const pages = old.pages.map((page, idx) => {
            if (idx !== 0) return page;
            const withoutOptimistic = page.filter(
              (m) => (m as OptimisticMessage).clientId !== clientId
            );
            return alreadyHasServerId
              ? withoutOptimistic
              : [serverMessage, ...withoutOptimistic];
          });
          return { ...old, pages };
        }
      );
      // Conversation preview/lastMessageAt likely changed.
      queryClient.invalidateQueries({ queryKey: ['messages', 'conversations'] });
    },

    onError: (_err, { conversationId }, ctx) => {
      const clientId = ctx?.clientId;
      if (!clientId) return;
      queryClient.setQueryData<MessagePages>(
        messageKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          const pages = old.pages.map((page, idx) => {
            if (idx !== 0) return page;
            return page.map((m) =>
              (m as OptimisticMessage).clientId === clientId
                ? ({ ...m, clientStatus: 'failed' } as OptimisticMessage as Message)
                : m
            );
          });
          return { ...old, pages };
        }
      );
    },
  });
}

// =====================================================
// MARK AS READ
// =====================================================

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { conversationId: string; messageIds: string[] }
  >({
    mutationFn: ({ conversationId, messageIds }) =>
      messagesApi.markAsRead(conversationId, messageIds),
    onMutate: async ({ conversationId, messageIds }) => {
      const ids = new Set(messageIds);
      const now = new Date().toISOString();
      queryClient.setQueryData<MessagePages>(
        messageKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) =>
              page.map((m) => (ids.has(m.id) && !m.readAt ? { ...m, readAt: now } : m))
            ),
          };
        }
      );
      // Optimistically zero the unread badge for this convo.
      queryClient.setQueriesData<Conversation[]>(
        { queryKey: ['messages', 'conversations'] },
        (old) =>
          old?.map((c) =>
            c.id === conversationId ? { ...c, unreadCount: 0 } : c
          )
      );
    },
  });
}

// =====================================================
// WEBSOCKET SUBSCRIPTIONS
// =====================================================

/**
 * Wire WS events into the React Query cache. Call once at app root (or
 * inside the Messages page) — multiple calls are safe but redundant.
 *
 * Events:
 *   - `message:new`     → append to the open conversation's first page,
 *                        de-duped by id; bump conversation preview.
 *   - `message:typing`  → ephemeral state, exposed via `useTypingIndicator`.
 *   - `message:read`    → patch readAt timestamps in the cache.
 */
export function useMessagesWebSocket(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubs: Array<() => void> = [];

    unsubs.push(
      wsService.on<Message>('message:new', (msg) => {
        queryClient.setQueryData<MessagePages>(
          messageKeys.messages(msg.conversationId),
          (old) => {
            if (!old) {
              return { pages: [[msg]], pageParams: [undefined] };
            }
            const exists = old.pages.some((p) => p.some((m) => m.id === msg.id));
            if (exists) return old;
            const pages = old.pages.map((page, idx) =>
              idx === 0 ? [msg, ...page] : page
            );
            return { ...old, pages };
          }
        );
        queryClient.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      })
    );

    unsubs.push(
      wsService.on<{
        conversationId: string;
        messageIds: string[];
        readAt: string;
      }>('message:read', ({ conversationId, messageIds, readAt }) => {
        const ids = new Set(messageIds);
        queryClient.setQueryData<MessagePages>(
          messageKeys.messages(conversationId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((m) => (ids.has(m.id) ? { ...m, readAt } : m))
              ),
            };
          }
        );
      })
    );

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [queryClient]);
}

/**
 * Per-conversation typing indicator. Receives WS `message:typing` events
 * scoped to `conversationId` and exposes a boolean that auto-clears 5s
 * after the last event.
 */
export function useTypingIndicator(conversationId: string | undefined): {
  isTyping: boolean;
  typingName: string | null;
} {
  const [state, setState] = useState<{ isTyping: boolean; typingName: string | null }>({
    isTyping: false,
    typingName: null,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    const unsub = wsService.on<{
      conversationId: string;
      senderRole: 'student' | 'instructor';
      senderName: string;
    }>('message:typing', (evt) => {
      if (evt.conversationId !== conversationId) return;
      // We only care about the *other* side typing.
      if (evt.senderRole === 'instructor') return;
      setState({ isTyping: true, typingName: evt.senderName });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setState({ isTyping: false, typingName: null });
      }, 5000);
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [conversationId]);

  return state;
}

/**
 * Helper for the composer: emit a throttled "I'm typing" ping to the server.
 * Throttled to once every 2s while the user is actively keystroking.
 */
export function useEmitTyping(conversationId: string | undefined) {
  const lastSentRef = useRef(0);
  return useCallback(() => {
    if (!conversationId) return;
    const now = Date.now();
    if (now - lastSentRef.current < 2000) return;
    lastSentRef.current = now;
    wsService.send('message:typing', { conversationId });
  }, [conversationId]);
}

/**
 * Convenience helper for callers that just want to ensure a conversation
 * exists for a given studentId before navigating to the thread.
 */
export function useStartConversation() {
  const queryClient = useQueryClient();
  return useMutation<Conversation, Error, string>({
    mutationFn: (studentId) => messagesApi.startConversation(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'conversations'] });
    },
  });
}
